import { Grid, TextField, Typography } from "@mui/material";
import * as React from "react";
import { EventEditUrgencyClassField } from "ui/Scripts/source/components/event/EventEditUrgencyClassField/EventEditUrgencyClassField";
import { IFileAttachment } from "ui/Scripts/source/models/common/fileAttachment";
import { IIdColorTitle } from "ui/Scripts/source/models/common/idColorTitle";
import { IIdTitleNode, IdTitleNode } from "ui/Scripts/source/models/common/idTitleNode";
import { ICompanyMenuItem } from "ui/Scripts/source/models/company/companyMenuItem";
import { IEventSettingSimple } from "ui/Scripts/source/models/eventSettings/eventSettingSimple";
import { Translations } from "ui/Scripts/source/models/translations";
import { IModifyUser } from "ui/Scripts/source/models/user/modifyUser";
import { IEventEditItem } from "../../../models/event/eventEditItem";
import { AttachmentList } from "../../attachments/AttachmentList";
import { DatePicker } from "../../framework/DateTime/DatePicker";
import { DialogSectionCard } from "../../framework/Dialog/DialogSectionCard";
import { LongText } from "../../framework/Form/LongText";
import { TextFieldWithButtonGroup } from "../../framework/Form/TextFieldWithButtonGroup";
import { FormField } from "../../../../../../QTeamSystems.QTeamIntra.Ui/Scripts/source/components/framework/FormField";
import { GridRow } from "../../framework/Grid/GridRow";
import { AvatarName } from "../../framework/User/AvatarName";
import { TimePicker } from "source/components/framework/DateTime";
import { Base } from "ui/Scripts/source/framework/base";

export interface IEventEditDialogBasicInformationProp {
    companyId: string;
    eventSetting: IEventSettingSimple;
    event: IEventEditItem;
    createCompany: ICompanyMenuItem;
    companies: ICompanyMenuItem[];
    units: IIdTitleNode[];
    urgencyClasses: IIdColorTitle[];
    hasBeenApproved: boolean;
    isSuperAdmin: boolean;
    canHandleEvent: boolean;
    canEditIdentificationData: boolean;
    isLockedToOtherUser: boolean;
    userIsCreator: boolean;
    isClosed: boolean;
    // General
    dateStr: string;
    timeStr: string;
    unitId: string;
    unitCode: string;
    urgencyClassId: string;
    createCompanyUnitCode: string;
    name: string;
    observer: string;
    observerAction: string;
    description: string;
    attachments: IFileAttachment[];
    lockedUser: IModifyUser;
    firstApproveUser: IModifyUser;
    lastApproveUser: IModifyUser;
    onChange: (name: string, value: string) => void;
    onSelectUnit: () => void;
    onRemoveAttachment: (id: string) => void;
    onDownloadAttachment: (id: string) => void;
    onAddAttachments: (fileList: FileList) => void;
    onUnlock: () => void;
}

export class EventEditDialogBasicInformation extends React.Component<IEventEditDialogBasicInformationProp, {}> {
    handleChange = (event) => {
        const target = event.target;
        this.props.onChange(target.name, target.value);
    };
    
    render() {
        const props = this.props;
        const event = props.event;
        if (!event) return null;
        const isPublished = event.isPublished();
        const receiverCompany = props.companies.find(i => i.id === event.companyId);
        const activeCompanyIsReceivingCompany = props.companyId === event.companyId;
        const senderCompanyIsReceivingCompany = event.createCompanyId === event.companyId;
        const basicInformationIsEditable = !props.isLockedToOtherUser && (props.isSuperAdmin || props.userIsCreator) && !props.hasBeenApproved;
        const urgencyClassIsEditable = !props.isLockedToOtherUser && activeCompanyIsReceivingCompany &&
            (!isPublished || ((props.isSuperAdmin || props.userIsCreator) && !props.hasBeenApproved) || (!props.isClosed && props.canHandleEvent));
        const unitIsVisible = !activeCompanyIsReceivingCompany || (activeCompanyIsReceivingCompany && !isPublished);
        const unitIsEditable = !props.isLockedToOtherUser && IdTitleNode.getFirstActiveNode(props.units) && (!isPublished || props.canEditIdentificationData);
        const attachmentsAreEditable = props.isSuperAdmin || props.userIsCreator;
        return (
            <GridRow sx={{ height: "100%" }}>
                <Grid item sm={12} md={8} sx={{ height: "calc(100% + 16px)" }}>
                    <DialogSectionCard headerTitle={Translations.Event} overflowY="auto">
                        {!senderCompanyIsReceivingCompany &&
                            <GridRow>
                                <Grid item sm={12} md={6}>
                                    <FormField title={Translations.Sender} margin="0 0 1.5rem 0">
                                        <AvatarName
                                            name={props.createCompany.name}
                                            picture={props.createCompany.logo}
                                        />
                                    </FormField>
                                </Grid>
                                <Grid item sm={12} md={6}>
                                    <FormField title={Translations.Receiver} required margin="0 0 1.5rem 0">
                                        <AvatarName
                                            name={receiverCompany.name}
                                            picture={receiverCompany.logo}
                                        />
                                    </FormField>
                                </Grid>
                            </GridRow>
                        }
                        <GridRow>
                            <Grid item sm={12} lg={6} xl={3}>
                                <FormField title={Translations.Date} required margin={0}>
                                    {(basicInformationIsEditable
                                        ? <DatePicker
                                            value={props.dateStr}
                                            onChange={(value: string) => props.onChange("date", value)}
                                        />
                                        : <TextField type="text" title={Translations.Date} value={props.dateStr + (!Base.isZeroTimeStr(props.timeStr) ? " " + props.timeStr : "")} disabled={true} />
                                    )}
                                </FormField>
                            </Grid>
                            {basicInformationIsEditable &&
                                <Grid item sm={12} lg={6} xl={3}>
                                    <FormField title={Translations.Time} fullWidth required margin="0">
                                        <TimePicker
                                            value={props.timeStr}
                                            onChange={(value: string) => props.onChange("time", value)}
                                        />
                                    </FormField>
                                </Grid>
                            }
                            <Grid item sm={12} lg={6} xl={3}>
                                <FormField title={Translations.Code} margin={0}>
                                    <TextField type="text" title={Translations.Code} value={props.event.code} disabled={true} />
                                </FormField>
                            </Grid>
                            <Grid item sm={12} lg={6} xl={3}>
                                <EventEditUrgencyClassField
                                    disabled={!urgencyClassIsEditable}
                                    urgencyClasses={props.urgencyClasses}
                                    urgencyClassId={props.urgencyClassId}
                                    onChange={(id) => props.onChange("urgencyClassId", id)}
                                />
                            </Grid>
                            {unitIsVisible &&
                                <Grid item sm={12} lg={6} xl={6}>
                                    <FormField title={Translations.Unit} margin={0}>
                                        <TextFieldWithButtonGroup
                                            type="text"
                                            title={Translations.Unit}
                                            value={props.createCompanyUnitCode}
                                            disabled={true}
                                            buttons={[
                                                { label: Translations.Select, onClick: props.onSelectUnit, hidden: !unitIsEditable }
                                            ]}
                                        />
                                    </FormField>
                                </Grid>
                            }
                        </GridRow>
                        <GridRow>
                            <Grid item xs>
                                <FormField title={Translations.Title} required>
                                    <TextField type="text" name="name" title={Translations.Title} inputProps={{ maxLength: 100 }} value={props.name} onChange={this.handleChange} disabled={!basicInformationIsEditable} />
                                </FormField>
                            </Grid>
                        </GridRow>
                        <GridRow>
                            {props.eventSetting.locationIsInUse() && !!event.locationName &&
                                <Grid item sm={6}>
                                    <FormField title={Translations.Location}>
                                        <Typography>{event.locationName}</Typography>
                                    </FormField>
                                </Grid>
                            }
                            <Grid item sm={(props.eventSetting.locationIsInUse() && !!event.locationName) ? 6 : 12}>
                                <FormField title={Translations.Observer}>
                                    <TextField type="text" name="observer" title={Translations.Observer} inputProps={{ maxLength: 100 }} value={props.observer} onChange={this.handleChange} disabled={!basicInformationIsEditable} />
                                </FormField>
                            </Grid>
                        </GridRow>
                        <GridRow>
                            <Grid item sm={props.eventSetting.observerActionIsInUse() ? 6 : 12}>
                                <LongText
                                    classes="form-group"
                                    disabled={!basicInformationIsEditable}
                                    expandable={true}
                                    required={true}
                                    title={Translations.Description}
                                    maxLength={10000}
                                    name="description"
                                    value={props.description}
                                    valueClasses="mSize"
                                    onChange={this.handleChange}
                                />
                            </Grid>
                            {props.eventSetting.observerActionIsInUse() &&
                                <Grid item xs>
                                    <LongText
                                        classes="form-group"
                                        disabled={!basicInformationIsEditable}
                                        expandable={true}
                                        required={props.eventSetting.observerActionIsRequired()}
                                        title={Translations.ObserverAction}
                                        maxLength={10000}
                                        name="observerAction"
                                        value={props.observerAction}
                                        valueClasses="mSize"
                                        onChange={this.handleChange}
                                    />
                                </Grid>
                            }
                        </GridRow>
                    </DialogSectionCard>
                </Grid>
                <Grid item sm={12} md={4} sx={{ height: "calc(100% + 16px)" }}>
                    <DialogSectionCard headerTitle={Translations.Attachments} overflowY="hidden">
                        <AttachmentList
                            imageSize={props.eventSetting.attachmentImageSize}
                            margin={0}
                            title={null}
                            attachments={props.attachments.filter(i => i.category === 0)}
                            isReadOnly={!attachmentsAreEditable}
                            lateThreshold={props.firstApproveUser?.date}
                            onRemoveAttachment={props.onRemoveAttachment}
                            onDownloadAttachment={props.onDownloadAttachment}
                            onAddAttachments={props.onAddAttachments}
                        />
                    </DialogSectionCard>
                </Grid>
            </GridRow>
        );
    }
}